import {
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  ElementRef,
  Inject,
  Input,
  LOCALE_ID,
  OnInit,
  SecurityContext,
  ViewChild,
  ViewContainerRef,
  OnDestroy,
  HostListener,
} from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { ActivatedRoute, Router, NavigationStart } from "@angular/router";
import { Location } from "@angular/common";
import { Observable, Subject, Subscription, interval, of } from "rxjs";
import { AppConstants } from "src/app/constants/constants";
import { Modes } from "src/app/constants/modes";
import { ScormEntry } from "src/app/_models/ScormEntry";
import { ScormSession } from "src/app/_models/ScormSession";
import { PublicContentObjectService } from "src/app/_services/public-content-object.service";
import { PublicScreenerResultService } from "src/app/_services/public-screener-result.service";
import { ScormReceiverService } from "src/app/_services/scorm-receiver.service";
import { ScormNavCloseComponent } from "../scorm-nav-close/scorm-nav-close.component";
import { ScormCloseService } from "src/app/_services/scorm-close.service";
import { BaseComponent } from "../base/base-component.component";
import { PublicUserSessionService } from "src/app/_services/public-user-session.service";
import { APP_CONFIG } from "src/app/lib/angular/injection-tokens";
import { IAppConfig } from "src/app/lib/config/app-config";
import { Idle } from "@ng-idle/core";
import { LearningTrackingService } from 'src/app/_services/learning-tracking.service';
import { catchError, map, switchMap, takeUntil, takeWhile } from "rxjs/operators";
import { SkillsCheckContent } from "src/app/_models/SkillsCheck";
import { Store } from "@ngrx/store";
import { AssignSkillsCheckContent } from "src/app/_store/learning-plan/learning-plan.actions";
import { UserSessionService } from "src/app/_services/user-session.service";
import { convertTimeStringToSeconds, getFormattedTimeString } from "src/app/shared/date-and-time-format";
import { MsalService } from "@azure/msal-angular";
import { ScormExitSaveService } from "src/app/_services/scorm-exit-save.service";

declare function SCORMapi1_2(params): void;
declare var API: any;

@Component({
  selector: "app-scorm-runtime12",
  templateUrl: "./scorm-runtime12.component.html",
  styleUrls: ["./scorm-runtime12.component.scss"],
})
export class ScormRuntime12Component extends BaseComponent
  implements OnInit, OnDestroy {
  @Input() contentUrl: string;
  @ViewChild("iframe", { static: false }) iframe: ElementRef;

  siteLanguageCode: string;
  contentLang: string;
  mode: number;
  lclId: number;
  lcStartLevel: string;
  questionId: string;
  courseId: number;
  topicId: number;
  scormSessionId: number;
  panelState: boolean[];
  _routeListener: Subscription;
  isDeactivated: boolean = false;
  fromLearningRecordTab: boolean = false;
  fromLearningPlan: boolean = false;
  fromViewLearningPlan: boolean = false;
  learnerId: number;
  iframeSub: Subscription;
  elapsedScormTime: number;
  previousTotalScormTimeString: string;
  timerSub: Subscription;
  contentLoaded: boolean;
  tokenRefreshSub$: Subscription;

  constructor(
    private idle: Idle,
    public sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private router: Router,
    private receiver: ScormReceiverService,
    private learnerTrackingService: LearningTrackingService,
    private screenerReceiver: PublicScreenerResultService,
    private vcRef: ViewContainerRef,
    private resolver: ComponentFactoryResolver,
    private contentService: PublicContentObjectService,
    private location: Location,
    private publicUserSessionService: PublicUserSessionService,
    private scormCloseService: ScormCloseService,
    private store: Store,
    private userSessionService: UserSessionService,
    private authService: MsalService,
    private scormExitSave: ScormExitSaveService,
    @Inject(LOCALE_ID) protected localeId: string,
    @Inject(APP_CONFIG) private config: IAppConfig
  ) {
    super();
    this.siteLanguageCode = localeId;
    this.panelState =
      this.location.getState()[AppConstants.SAVED_QSET_PANEL_STATE];
    this._routeListener = this.router.events.subscribe((event: NavigationStart) => {
      if(event.navigationTrigger === AppConstants.POPSTATE){
        this.scormCloseService.closeScorm(this.panelState);
      }
      else if(event.navigationTrigger === AppConstants.IMPERATIVE){
        if(!this.checkScormIfFullyLoaded())
          this.clearScorm();
      }

      if (this.compRef) {
        this.compRef.instance.checkScormResult();
      }
    });
  }
  
  canDeactivate(): boolean | Observable<boolean> {
    return this.isDeactivated;
  }

  doc;
  compRef: ComponentRef<ScormNavCloseComponent>;
  safeURL: SafeResourceUrl = undefined;
  objectId: string = undefined;
  sessions: { [name: string]: ScormSession } = {};
  ready: boolean = false;
  metadataReady: boolean = false;
  saving: boolean = false;
  readyToLoad$ = new Subject<void>();
  isTracked: boolean = true;
  emptyEntries: Array<ScormEntry> = [];
  isComplete: boolean = false;
  hasFlushed: boolean = false;
  hasSentScreenerResult: boolean = false;
  isSavingScreenerResult: boolean = false;
  private cachedAccessToken: string;
  private readonly originalWindowClose: () => void = window.close;

  ngOnInit(): void {
    window.close = (): void => this.onContentClose();

    this.readyToLoad$.subscribe((x) => {
      this.ready = false;
      this.metadataReady = false;
      this.clearUnrelatedParams(this.mode);
      this.scormSessionId = 0;
      this.hasSentScreenerResult = false;
      this.isSavingScreenerResult = false;

      switch (this.mode) {
        case Modes.Practice:
        case Modes.Lesson:
          if(this.fromViewLearningPlan) {
            this.executeLearnerSession();
          } else {
            this.execUntrackedScormplayer();
          }
          break;
        case Modes.LessonTracked:
          this.execTrackedScormPlayerLesson();
          break;
        case Modes.SkillCheck:
          this.execTrackedScormPlayerSkillCheck();
          break;
        case Modes.Screener:
          this.execScormPlayerScreenerMode();
          break;
        case Modes.Review:
          this.execReviewQuestion();
          break;
        case Modes.ReviewSkillsCheck:
          this.execReviewQuestion(Modes.ReviewSkillsCheck);
          break;
        case Modes.Tracked:
        default:
          this.execTrackedScormPlayer();
          break;
      }

      //Trigger scroll to top from base component for UX.
      this.scrollToTop();
    });

    this.route.queryParamMap.subscribe((params) => {
      this.mode = +params.get(AppConstants.SCORM_MODE);
      this.lclId = +params.get(AppConstants.LCL_ID);
      this.lcStartLevel = params.get(AppConstants.LC_START_LEVEL);
      this.questionId = params.get(AppConstants.QUESTION_ID);
      this.courseId = +params.get(AppConstants.COURSE_ID);
      this.topicId = +params.get(AppConstants.TOPIC_ID);
      this.fromLearningRecordTab = JSON.parse(params.get(AppConstants.FROM_LEARNING_RECORD_TAB));
      this.fromLearningPlan = JSON.parse(params.get(AppConstants.FROM_LEARNING_PLAN));

      if(params.get(AppConstants.FROM_VIEW_LEARNING_PLAN)) {
        this.fromViewLearningPlan = JSON.parse(params.get(AppConstants.FROM_VIEW_LEARNING_PLAN));
      }
      if(params.get(AppConstants.LEARNER_ID)) {
        this.learnerId = +params.get(AppConstants.LEARNER_ID);
      }
      if(params.get(AppConstants.LEARNER)) {
        this.learnerId = this.learnerId ? this.learnerId : +params.get(AppConstants.LEARNER);
      }
    });

    this.route.params.subscribe(() => {
      this.objectId = this.route.snapshot.paramMap.get(AppConstants.OBJECT_ID);
      this.safeURL = undefined;
      this.ready = false;
      this.sessions[this.objectId] = undefined;
      this.readyToLoad$.next();
    });

    let scormWrapper = document.querySelector(AppConstants.SCORM_WRAPPER) as HTMLElement;
    scormWrapper.style.visibility = AppConstants.COLLAPSE;
  }

  onLoad(iframe) {
    this.doc = iframe.contentDocument || iframe.contentWindow;
    let body = this.doc.querySelector(AppConstants.BODY);
    if (body.textContent.includes(AppConstants.NOTFOUND_404)) {
      this.toggleLoaderAndIFrame();
    }

    this.createScormNavCloseComponent();

    if ([Modes.Tracked, Modes.LessonTracked, Modes.SkillCheck].includes(this.mode)) {
      this.startTokenRefresh();
    }

    this.iframeSub = interval(1000).pipe(
        takeWhile(() => !this.contentLoaded, true)
      )
      .subscribe({
        next: () => {
          this.contentLoaded = this.checkScormIfFullyLoaded();
        },
        complete: () => {
          this.startTimer();
        }
      });
  }

  startTimer(): void{
    this.stopTimer();
    this.elapsedScormTime = 0;

    this.timerSub = interval(1000).subscribe(() => {
      this.elapsedScormTime++;
    });
  }

  stopTimer(): void {
    if (this.timerSub) {
      this.timerSub.unsubscribe();
      this.timerSub = undefined;
    }
  }

  setSessionTime(entries: ScormEntry[], elapsedScormTime: number, previousTotalScormTimeString: string): ScormEntry[] {
    entries.forEach(item => {
      if(item.elementName == AppConstants.CORE_SESSION_TIME)
      {
        item.elementValue = getFormattedTimeString(elapsedScormTime);
      }
    });

    let totalTimeEntry = entries.find(i => i.elementName == AppConstants.CORE_TOTAL_TIME)
    let initialTotalTimeValue = previousTotalScormTimeString.indexOf(AppConstants.PERIOD) === -1 ? 
        previousTotalScormTimeString : previousTotalScormTimeString.substring(0, previousTotalScormTimeString.indexOf(AppConstants.PERIOD));
    
    if(totalTimeEntry){
      totalTimeEntry.elementValue = getFormattedTimeString(elapsedScormTime + convertTimeStringToSeconds(initialTotalTimeValue));
    }
    else if(entries.find(i => i.elementName === AppConstants.CORE_SESSION_TIME)){
      let newEntry = new ScormEntry();
      newEntry.scormSessionId = entries[0].scormSessionId;
      newEntry.elementName = AppConstants.CORE_TOTAL_TIME;
      newEntry.elementValue = getFormattedTimeString(elapsedScormTime + convertTimeStringToSeconds(initialTotalTimeValue));;
      entries.push(newEntry);      
    }

    return entries;
  }
   
  createScormNavCloseComponent() {
    const compFactory = this.resolver.resolveComponentFactory(
      ScormNavCloseComponent
    );
    this.compRef = this.vcRef.createComponent(compFactory);
    this.compRef.location.nativeElement.id = "innerComp";

    let menuClose = this.doc.querySelector("li.menuClose");

    if (menuClose != null) {
      this.toggleLoaderAndIFrame();
      menuClose.appendChild(this.compRef.location.nativeElement);
      this.doc.querySelector(".menuClose > a").remove();

      //add event listener to interrupt idle time when user has an activity
      if (typeof this.doc.addEventListener !== undefined) {
        const events = AppConstants.DOCUMENT_INTERRUPT_CONFIG.split(AppConstants.SINGLE_SPACE);
        events.forEach((event:string) => {
          this.doc.addEventListener(event, () => {  
            this.idle.interrupt(true);
            this.idle.watch();
          });           
        });

      }
    }

    if (this.doc != null) {
      this.compRef.instance.iframeData = this.doc;
    }

  }

  private toggleLoaderAndIFrame() {
    let scormLoader = document.querySelector(AppConstants.SCORM_LOADER) as HTMLElement;
    scormLoader.remove();
    let scormWrapper = document.querySelector(AppConstants.SCORM_WRAPPER) as HTMLElement;
    scormWrapper.style.visibility = AppConstants.VISIBLE;
    this.ready = true;
  }

  private execTrackedScormPlayerLesson() {
    this.receiver.createSessionByObjectId$(this.objectId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((session) => {      
        this.scormSessionId = session.id;
          
        if(this.sessions[this.objectId] !== undefined)
          if(this.sessions[this.objectId].open === false)
            return this.clearScorm();
        
        this.sessions[this.objectId] = session;
        this.safeURL = this.sanitizer.bypassSecurityTrustResourceUrl(
          this.getSanitizedUrl(session.contentObjectPath)
        );

        this.sessions[this.objectId].mode = this.mode;
        this.sessions[this.objectId].learnerCourseLogId = this.lclId;
        this.sessions[this.objectId].open = true;

        let scormParams: { [name: string]: any } = {};

        this.configureScormParams(scormParams);

        API = undefined;
        API = new SCORMapi1_2(scormParams);
        API.RegisterCallback(this.markVisitedAssignSCContentAndSaveState, this, this.objectId);

        this.metadataReady = true;
      });
  }

  private execTrackedScormPlayer() {      
    if(this.sessionCompletionInProgressStatus()) {
        this.initializeScormAPIMetadataByLastSession();
        return;
    }

    this.receiver
      .createSessionByObjectId$(this.objectId)
      .subscribe((session) => {
        this.sessions[this.objectId] = session;
        this.safeURL = this.sanitizer.bypassSecurityTrustResourceUrl(
          this.getSanitizedUrl(session.contentObjectPath)
        );

        this.sessions[this.objectId].mode = this.mode;
        this.sessions[this.objectId].learnerCourseLogId = this.lclId;
        this.sessions[this.objectId].open = true;

        let scormParams: { [name: string]: any } = {};

        this.configureScormParams(scormParams);

        API = undefined;
        API = new SCORMapi1_2(scormParams);
        API.RegisterCallback(this.saveState, this, this.objectId);

        this.metadataReady = true;
      });
  }

  private  execTrackedScormPlayerSkillCheck() {
    if(this.fromViewLearningPlan) {
      this.receiver.getSessionByLearnerIdAndObjectId$(this.learnerId, this.objectId).pipe(
        map((session: ScormSession | null) => {
          return session ?? null;
        }),
        catchError((_) => {
          return of(null);
        })
      )
        .subscribe((session: ScormSession) => {
          if(session) {
            this.sessions[this.objectId] = session;
            this.safeURL = this.sanitizer.bypassSecurityTrustResourceUrl(
              this.getSanitizedUrl(session.contentObjectPath)
            );
            this.sessions[this.objectId].mode = this.mode;
            this.sessions[this.objectId].learnerCourseLogId = this.lclId;
            this.sessions[this.objectId].open = true;
            this.sessions[this.objectId].scormEntries = session.scormEntries;
 
            let scormParams: { [name: string]: any } = {};
    
            this.configureScormParams(scormParams);
            API = undefined;
            API = new SCORMapi1_2(scormParams);
            API.RegisterCallback(this.saveState, this, this.objectId);
            this.metadataReady = true;
          } 
          else {
            this.execScormPlayerScreenerMode(false);
          }
        });
    } else {
      if(this.sessionCompletionInProgressStatus()){
        this.initializeScormAPIMetadataByLastSession();
        return;
      }

      this.learnerTrackingService.createSessionByObjectIdSkillCheck$(this.objectId)
      .subscribe((session) => {
        this.sessions[this.objectId] = session;
        this.safeURL = this.sanitizer.bypassSecurityTrustResourceUrl(
          this.getSanitizedUrl(session.contentObjectPath)
        );

        this.sessions[this.objectId].mode = this.mode;
        this.sessions[this.objectId].learnerCourseLogId = this.lclId;
        this.sessions[this.objectId].open = true;

        let scormParams: { [name: string]: any } = {};

        this.configureScormParams(scormParams);

        API = undefined;
        API = new SCORMapi1_2(scormParams);
        API.RegisterCallback(this.saveState, this, this.objectId);

        this.metadataReady = true;
      });
    }

  }

  private execReviewQuestion(mode?: Modes) {

    let sub: Observable<ScormSession> = this.fromViewLearningPlan ? 
      this.receiver.getSessionByLearnerIdAndObjectId$(this.learnerId, this.objectId) :
      this.receiver.getSessionByObjectId$(this.objectId);

    sub.subscribe((session: ScormSession) => {
      this.sessions[this.objectId] = session;
      var qId = this.parseQuestionId(this.questionId);
      var fullUrl =
        session.contentObjectPath + 
          (mode === Modes.ReviewSkillsCheck ? 
            AppConstants.SCORM_SC_QUERY_PARAM_PREFIX :
            AppConstants.SCORM_QUERY_PARAM_PREFIX) 
          + qId;
      this.safeURL = this.sanitizer.bypassSecurityTrustResourceUrl(
        this.getSanitizedUrl(fullUrl)
      );

      this.sessions[this.objectId].mode = this.mode;
      
      const scormEntryLaunchData: ScormEntry = 
      { id: 0, 
        scormSessionId: this.sessions[this.objectId].id,
        elementName: this.fromViewLearningPlan ? AppConstants.BLANK : AppConstants.CORE_LAUNCH_DATA,
        elementValue: AppConstants.LEARNER,
        dateCreated: null,
        dateModified: null,
        learnerCourseLogId: 0 
      };

      if(mode === Modes.ReviewSkillsCheck){
        this.sessions[this.objectId].scormEntries.push(scormEntryLaunchData);
      }

      let scormParams: { [name: string]: any } = {};

      this.configureScormParams(scormParams);

      API = undefined;
      API = new SCORMapi1_2(scormParams);
      API.RegisterCallback(this.saveState, this, this.objectId);

      this.metadataReady = true;
    });
  }

  private execScormPlayerScreenerMode(open: boolean = true) {
    this.contentService
      .getPathByObjectId$(this.objectId)
      .subscribe((contentPath) => {
        this.safeURL = this.sanitizer.bypassSecurityTrustResourceUrl(
          this.getSanitizedUrl(contentPath)
        );
        this.sessions[this.objectId] = new ScormSession();
        this.sessions[this.objectId].mode = this.mode;
        this.sessions[this.objectId].scormEntries = this.emptyEntries;
        if(open) {
          this.sessions[this.objectId].open = true;
        }

        let scormParams: { [name: string]: any } = {};

        this.configureScormParams(scormParams);

        API = undefined;
        API = new SCORMapi1_2(scormParams);
        API.RegisterCallback(this.saveState, this, this.objectId);

        this.metadataReady = true;
      });
  }

  private executeLearnerSession(): void {
    this.receiver.getSessionByLearnerIdAndObjectId$(this.learnerId, this.objectId).pipe(
      map((result) => { return result ?? null; }),
      catchError((_) => {
        return of(null);
      })
    )
      .subscribe((session: ScormSession) => {
        if(session) {
          this.safeURL = this.sanitizer.bypassSecurityTrustResourceUrl(
            this.getSanitizedUrl(session.contentObjectPath)
          );
          this.sessions[this.objectId] = new ScormSession();
          this.sessions[this.objectId].mode = this.mode;
          this.sessions[this.objectId].scormEntries = session.scormEntries;
  
          let scormParams: { [name: string]: any } = {};
          this.configureScormParams(scormParams);
  
          API = undefined;
          API = new SCORMapi1_2(scormParams);
          API.RegisterCallback(this.saveState, this, this.objectId);
  
          this.metadataReady = true;
        } else {
          this.execUntrackedScormplayer();
        }
      });
  }

  private execUntrackedScormplayer() {
    this.contentService
      .getPathByObjectId$(this.objectId)
      .subscribe((contentPath) => {
        this.safeURL = this.sanitizer.bypassSecurityTrustResourceUrl(
          this.getSanitizedUrl(contentPath)
        );
        this.sessions[this.objectId] = new ScormSession();
        this.sessions[this.objectId].mode = this.mode;
        this.sessions[this.objectId].scormEntries = this.emptyEntries;

        let scormParams: { [name: string]: any } = {};
        this.configureScormParams(scormParams);

        API = undefined;
        API = new SCORMapi1_2(scormParams);
        API.RegisterCallback(this.saveState, this, this.objectId);

        this.metadataReady = true;
      });
  }

  markVisitedAssignSCContentAndSaveState(
    param: string,
    rThis: ScormRuntime12Component,
    objectId: string): number {
      
      if(!rThis.checkScormIfFullyLoaded()){        
        this.clearScorm();
        return 200;
      }

      if (!rThis.sessions[objectId].open) return 200;
      
      rThis.mapEntry(param, rThis, objectId);
      let isExit =
        rThis.getEntry(rThis.sessions[objectId], AppConstants.CORE_EXIT) !=
        undefined &&
        rThis.getEntry(rThis.sessions[objectId], AppConstants.CORE_EXIT)
          .elementValue === AppConstants.SUSPEND;

      if(isExit){
        rThis.learnerTrackingService
          .updateLearningObjectDateVisitedByObjectId$(rThis.objectId)
          .pipe(
            switchMap(() => {            
              if (rThis.topicId != 0 && (rThis.fromLearningPlan || rThis.fromLearningRecordTab)
                  && rThis.sessions[objectId].mode === Modes.LessonTracked)
              {
                var skillsCheckContent: SkillsCheckContent = {
                  learnerLevelId: rThis.topicId,
                  courseId: rThis.courseId,
                };               
                
                rThis.store.dispatch(AssignSkillsCheckContent({skillsCheckContent: skillsCheckContent, courseId: rThis.courseId}));
              }
              
              return of({});
            })
          )
          .subscribe(() => {
            rThis.sessions[objectId].open = false;
          })
      }
      else return rThis.saveState(param, rThis, objectId);
  }

  saveState(
    param: string,
    rThis: ScormRuntime12Component,
    objectId: string
  ): number {       
    if(!rThis.checkScormIfFullyLoaded())
      return 200;

    if (rThis.sessions[objectId].open) {
      rThis.sessions[objectId].saving = true;
      rThis.mapEntry(param, rThis, objectId);

      if (!rThis.sessions[objectId].open) {
        return 200;
      } else {
        let isExit =
          rThis.getEntry(rThis.sessions[objectId], AppConstants.CORE_EXIT) !=
          undefined &&
          rThis.getEntry(rThis.sessions[objectId], AppConstants.CORE_EXIT)
            .elementValue === AppConstants.SUSPEND;
        if (isExit && !rThis.sessions[objectId].open) {
          return 200;
        }

        if (rThis.sessions[objectId].mode == Modes.Tracked || rThis.sessions[objectId].mode == Modes.LessonTracked || rThis.sessions[objectId].mode == Modes.SkillCheck) {
          if (!rThis.sessions[objectId].completed && !rThis.sessions[objectId].completed !== undefined && !rThis.fromViewLearningPlan && !rThis.sessionCompletionInProgressStatus()) {
            rThis.sessions[objectId].completed =
              rThis.getEntry(
                rThis.sessions[objectId],
                AppConstants.CORE_LESSON_STATUS
              ) != undefined &&
              rThis.getEntry(
                rThis.sessions[objectId],
                AppConstants.CORE_LESSON_STATUS
              ).elementValue === AppConstants.COMPLETED_STATUS;

              if((rThis.sessions[objectId].mode == Modes.SkillCheck || rThis.sessions[objectId].mode == Modes.Tracked) && rThis.sessions[objectId].completed)
              {
                rThis.userSessionService.onDisplayLoader$.next(true);
                sessionStorage.setItem(AppConstants.SCORM_COMPLETION_INPROGRESS_KEY, AppConstants.TRUE);
              }

            if (isExit) {
              rThis.sessions[objectId].scormEntries = rThis.setSessionTime(rThis.sessions[objectId].scormEntries, rThis.elapsedScormTime, rThis.previousTotalScormTimeString);
              rThis.sessions[objectId].open = false;
            } else {
              rThis.sessions[objectId].scormEntries = rThis.setSessionTime(rThis.sessions[objectId].scormEntries, rThis.elapsedScormTime, rThis.previousTotalScormTimeString);
              if(rThis.sessions[objectId].mode == Modes.LessonTracked) {
                rThis.learnerTrackingService
                .saveSessionStateLesson$(rThis.sessions[objectId])
                .subscribe((items) => {
                  rThis.sessions[objectId].saving = false;
                  rThis.sessions[objectId].scormEntries = items;

                  if (
                    rThis.objectId !=
                    rThis.route.snapshot.paramMap.get(AppConstants.OBJECT_ID) &&
                    rThis.sessions[objectId].open
                  ) {
                    rThis.readyToLoad$.next();
                  }
                });
              }
              else if(rThis.sessions[objectId].mode == Modes.SkillCheck) {
                rThis.learnerTrackingService
                .saveSessionStateSkillCheck$(rThis.sessions[objectId], rThis.courseId)
                .subscribe((items) => {
                  rThis.sessions[objectId].saving = false;
                  rThis.sessions[objectId].scormEntries = items;
                  if(rThis.userSessionService.onDisplayLoader$.value)
                  {
                    rThis.userSessionService.onDisplayLoader$.next(false);
                  }

                  if (
                    rThis.objectId !=
                    rThis.route.snapshot.paramMap.get(AppConstants.OBJECT_ID) &&
                    rThis.sessions[objectId].open
                  ) {
                    rThis.readyToLoad$.next();
                  }
                });
              }
              else {
                rThis.receiver
                .saveSessionState$(rThis.sessions[objectId])
                .subscribe((items) => {
                  rThis.sessions[objectId].saving = false;
                  rThis.sessions[objectId].scormEntries = items;
                  if(rThis.userSessionService.onDisplayLoader$.value && rThis.sessions[objectId].mode == Modes.Tracked)
                  {
                    rThis.userSessionService.onDisplayLoader$.next(false);
                  }

                  if (
                    rThis.objectId !=
                    rThis.route.snapshot.paramMap.get(AppConstants.OBJECT_ID) &&
                    rThis.sessions[objectId].open
                  ) {
                    rThis.readyToLoad$.next();
                  }
                });
              }
            }
          }
        }
        if (rThis.sessions[objectId].mode == Modes.Screener) {
          let isComplete =
            rThis.getEntry(
              rThis.sessions[objectId],
              AppConstants.CORE_LESSON_STATUS
            ) != undefined &&
            rThis.getEntry(
              rThis.sessions[objectId],
              AppConstants.CORE_LESSON_STATUS
            ).elementValue === AppConstants.COMPLETED_STATUS;

          if (isComplete && !rThis.hasSentScreenerResult) {
            rThis.setEntry(
              rThis.sessions[objectId],
              AppConstants.CORE_TOTAL_TIME,
              getFormattedTimeString(rThis.elapsedScormTime ?? 0)
            );
            rThis.saveScreenerResult(rThis, objectId);
          }
        }
      }
    }

    return 200;
  }

  private saveScreenerResult(rThis: ScormRuntime12Component, objectId: string) {
    // Get ScreenerId from sessionStorage
    let id: number = Number(sessionStorage.getItem(AppConstants.SCREENER_ID));

    rThis.sessions[objectId].objectId = objectId;

    if (!this.config.production) {
      if (id == undefined) {
        throw console.error(
          "Error saving the result. No screener record found."
        );
      }
    }
    rThis.hasSentScreenerResult = true;
    rThis.isSavingScreenerResult = true;
    rThis.screenerReceiver
      .saveScreenerResult$(id, rThis.sessions[objectId])
      .subscribe(
        () => {
          rThis.isSavingScreenerResult = false;
          rThis.sessions[objectId].saving = false;
          if (
            rThis.objectId !=
            rThis.route.snapshot.paramMap.get(AppConstants.OBJECT_ID) &&
            rThis.sessions[objectId].open
          ) {
            rThis.readyToLoad$.next();
          }
        },
        (error) => {
          rThis.hasSentScreenerResult = false;
          rThis.isSavingScreenerResult = false;
          if (!this.config.production)
            console.error(
              "Error encountered while saving the screener result.",
              error
            );
        }
      );
  }

  private getEntry(session: ScormSession, name: string): ScormEntry {
    return session.scormEntries.find((x) => x.elementName === name);
  }

  private removeEntry(session: ScormSession, name: string): void {
    session.scormEntries = session.scormEntries.filter(
      (x) => x.elementName !== name
    );
  }

  private mapEntry(param: string, rThis: ScormRuntime12Component, objectId: any): void{
    let params = param.split(AppConstants.AMPSERSAND);

    params.forEach((item) => {
      if (item !== "") {
        let vals = item.split(AppConstants.EQUALS);
        let re = new RegExp(AppConstants.DOUBLE_UNDERSCORE_DELIM, AppConstants.GLOBAL_MODIFIER);
        rThis.setEntry(
          rThis.sessions[objectId],
          vals[0].replace(re, AppConstants.PERIOD),
          decodeURIComponent(vals[1])
        );
      }
    });
  }

  private setEntry(session: ScormSession, name: string, value): void {
    if (value === undefined) {
      return;
    }

    if (session.scormEntries.find((x) => x.elementName === name)) {
      session.scormEntries.find((x) => x.elementName === name).elementValue = value;
    } else {
      let newEntry = new ScormEntry();
      newEntry.scormSessionId = session.id;
      newEntry.elementName = name;
      newEntry.elementValue = value;
      session.scormEntries.push(newEntry);
    }
  }

  private getSanitizedUrl(contentPath: string): string {
    if (
      this.siteLanguageCode &&
      this.siteLanguageCode != AppConstants.DEFAULT_LANG_CODE
    ) {
      contentPath = contentPath.replace(
        AppConstants.SCORM_LO_FILE,
        "/lo_" + this.siteLanguageCode + ".html"
      );
    }
    
    if (this.config.scormContentLocation) {
      contentPath = this.config.scormContentLocation + contentPath;
    }

    if (this.lcStartLevel) {
      contentPath = contentPath + "?first_route=" + this.lcStartLevel;
    }


    return this.sanitizer.sanitize(
      SecurityContext.URL,
      contentPath
    );
  }

  private configureScormParams(scormParams: { [name: string]: any }) {
    scormParams[AppConstants.SUSPEND_DATA] = this.getEntry(
      this.sessions[this.objectId],
      AppConstants.SUSPEND_DATA
    )
      ? this.getEntry(this.sessions[this.objectId], AppConstants.SUSPEND_DATA)
        .elementValue
      : AppConstants.BLANK;
    scormParams[AppConstants.CORE_ENTRY] = this.getEntry(
      this.sessions[this.objectId],
      AppConstants.CORE_ENTRY
    )
      ? this.getEntry(this.sessions[this.objectId], AppConstants.CORE_ENTRY)
        .elementValue
      : AppConstants.BLANK;
      
    scormParams[AppConstants.CORE_EXIT] = this.getEntry(
      this.sessions[this.objectId],
      AppConstants.CORE_EXIT
    )
      ? this.getEntry(this.sessions[this.objectId], AppConstants.CORE_EXIT)
        .elementValue
      : AppConstants.BLANK;

    scormParams[AppConstants.CORE_TOTAL_TIME] = this.getEntry(
      this.sessions[this.objectId],
      AppConstants.CORE_TOTAL_TIME
    )
      ? this.getEntry(
        this.sessions[this.objectId],
        AppConstants.CORE_TOTAL_TIME
      ).elementValue
      : AppConstants.DEFAULT_TIME;

    this.previousTotalScormTimeString = scormParams[AppConstants.CORE_TOTAL_TIME];

    scormParams[AppConstants.CORE_STUDENT_NAME] = this.getEntry(
      this.sessions[this.objectId],
      AppConstants.CORE_STUDENT_NAME
    )
      ? this.getEntry(
        this.sessions[this.objectId],
        AppConstants.CORE_STUDENT_NAME
      ).elementValue
      : sessionStorage.getItem(AppConstants.FIRST_NAME) +
      AppConstants.SINGLE_SPACE +
      sessionStorage.getItem(AppConstants.LAST_NAME);

    scormParams[AppConstants.CORE_STUDENTID_OR_DOB] = this.getEntry(
      this.sessions[this.objectId],
      AppConstants.CORE_STUDENTID_OR_DOB
    )
      ? this.getEntry(
        this.sessions[this.objectId],
        AppConstants.CORE_STUDENTID_OR_DOB
      ).elementValue
      : AppConstants.BLANK;
      
    scormParams[AppConstants.CORE_LAUNCH_DATA] = this.getEntry(
      this.sessions[this.objectId],
      AppConstants.CORE_LAUNCH_DATA
    )
      ? this.getEntry(
        this.sessions[this.objectId],
        AppConstants.CORE_LAUNCH_DATA
      ).elementValue
      : AppConstants.BLANK;

    this.removeEntry(this.sessions[this.objectId], AppConstants.CORE_EXIT);
    this.removeEntry(this.sessions[this.objectId], AppConstants.CORE_ENTRY);
  }

  private clearUnrelatedParams(mode: number) {
    if (mode != Modes.Review && mode != Modes.ReviewSkillsCheck) {
      this.questionId = undefined;
    }

    if (mode != Modes.Tracked) {
      this.lclId = undefined;
    }
  }

  private parseQuestionId(questionId: string): string {
    return questionId.split(AppConstants.UNDERSCORE_DELIM, 2)[1];
  }

  clearScorm(): void{
    if(this.sessions[this.objectId] === undefined)
    {
      this.sessions[this.objectId] = new ScormSession();
      this.sessions[this.objectId].open = false;
    }

    if(this.scormSessionId != 0 && this.scormSessionId != undefined
      && (this.mode == Modes.LessonTracked || this.mode == Modes.SkillCheck))
      {
        const sub$ = this.receiver.deleteSessionById$(this.scormSessionId)
          .subscribe(() => {
            this.scormSessionId = 0;            
          });    
        this.addSubscription(sub$)  
      }
  }

  checkScormIfFullyLoaded(): boolean{    
    let isFullyLoaded = false;
    const scormFrame = document.querySelector(AppConstants.SCORM_FRAME) as HTMLIFrameElement;

    if(scormFrame !== null && scormFrame !== undefined){
      const curtainImage = scormFrame?.contentWindow.document.body?.querySelector(AppConstants.SCORM_CURTAIN_IMAGE) as HTMLElement;

      if(curtainImage){
        if(curtainImage.style.display != AppConstants.NONE){
          isFullyLoaded = false;
        } else isFullyLoaded = true;
      }
    } else isFullyLoaded = false;

    return isFullyLoaded;
  }

  @HostListener(AppConstants.WIN_BEFOREUNLOAD, [AppConstants.EVENT_PROP])
  closeScormBeforeUnload(event?: BeforeUnloadEvent): void {
    if (this.isSavingScreenerResult) {
      event?.preventDefault();
      if (event) event.returnValue = true;
      return;
    }

    this.checkScormIfFullyLoaded();
    this.flushScormOnUnload();
  }

  private startTokenRefresh(): void {
    this.tokenRefreshSub$?.unsubscribe();
    this.cacheAccessToken();

    const intervalMs: number =
      this.config.tokenRefreshIntervalMs ||
      AppConstants.DEFAULT_TOKEN_REFRESH_INTERVAL_MS;

    this.tokenRefreshSub$ = interval(intervalMs).subscribe(() => {
      this.cacheAccessToken();
    });
  }

  private cacheAccessToken(): void {
    const account = this.authService.instance.getActiveAccount();
    if (!account) return;

    const sub$ = this.authService
      .acquireTokenSilent({ scopes: [this.config.protectedResourceDefaultScope], account })
      .subscribe({
        next: (result) => {
          this.cachedAccessToken = result.accessToken;
        },
        error: () => {},
      });
    this.addSubscription(sub$);
  }

  private prepareFlushSession(): ScormSession | null {
    if (this.hasFlushed) return null;
    if (typeof API === "undefined" || !this.metadataReady) return null;

    const session = this.sessions[this.objectId];
    if (!session || !session.open) return null;

    const persistingModes = [Modes.Screener, Modes.Tracked, Modes.LessonTracked, Modes.SkillCheck];
    if (!persistingModes.includes(session.mode)) return null;

    const isSaveSkipped: boolean =
      session.mode === Modes.Screener
        ? this.hasSentScreenerResult
        : session.completed ||
          this.fromViewLearningPlan ||
          this.sessionCompletionInProgressStatus();
    if (isSaveSkipped) return null;

    this.hasFlushed = true;
    return session;
  }

  flushScormOnUnload(): void {
    const session = this.prepareFlushSession();
    if (!session) return;

    try {
      this.syncSessionTime();

      const rThis = this;
      const objectId = this.objectId;
      const isComplete =
        API.LMSGetValue(AppConstants.CORE_LESSON_STATUS) === AppConstants.COMPLETED_STATUS;

      // Redirect SCORM persistence to a map-only sink so the finish/commit maps the latest
      // cmi (incl. total_time) into scormEntries without firing an async HttpClient save that
      // a real page unload would abandon; the keepalive transport below does the persisting.
      API.RegisterCallback(
        (param: string) => {
          rThis.mapEntry(param, rThis, objectId);
          return 200;
        },
        this,
        objectId
      );

      if (isComplete) {
        API.LMSFinish(AppConstants.BLANK);
      } else {
        API.LMSCommit(AppConstants.BLANK);
      }

      session.objectId = objectId;
      const screenerId = Number(sessionStorage.getItem(AppConstants.SCREENER_ID));
      this.scormExitSave.flushOnUnload(
        session,
        isComplete,
        screenerId,
        this.courseId,
        this.config,
        this.cachedAccessToken
      );
    } catch {
      // best-effort flush; never block unload
    }
  }

  sessionCompletionInProgressStatus(): boolean {
    return sessionStorage.getItem(AppConstants.SCORM_COMPLETION_INPROGRESS_KEY) === AppConstants.TRUE;
  }

  initializeScormAPIMetadataByLastSession(): any {
    this.receiver.getSessionByLearnerIdAndObjectId$(this.learnerId, this.objectId).pipe(
        map((session: ScormSession | null) => {
          return session ?? null;
        }),
        catchError((_) => {
          return of(null);
        })
      )
        .subscribe((session: ScormSession) => {
          if(session) {
            this.sessions[this.objectId] = session;
            this.safeURL = this.sanitizer.bypassSecurityTrustResourceUrl(
              this.getSanitizedUrl(session.contentObjectPath)
            );
            this.sessions[this.objectId].mode = this.mode;
            this.sessions[this.objectId].learnerCourseLogId = this.lclId;
            this.sessions[this.objectId].open = true;
            this.sessions[this.objectId].scormEntries = session.scormEntries;
 
            let scormParams: { [name: string]: any } = {};
    
            this.configureScormParams(scormParams);
            API = undefined;
            API = new SCORMapi1_2(scormParams);
            API.RegisterCallback(this.saveState, this, this.objectId);
            this.metadataReady = true;
          } 
        });
  }

  flushScormOnExit(): void {
    const session = this.prepareFlushSession();
    if (!session) return;

    try {
      this.syncSessionTime();

      const isComplete =
        API.LMSGetValue(AppConstants.CORE_LESSON_STATUS) === AppConstants.COMPLETED_STATUS;
      if (isComplete) {
        API.LMSFinish(AppConstants.BLANK);
      } else {
        API.LMSCommit(AppConstants.BLANK);
      }
    } catch {
      // best-effort flush; never block teardown
    }
  }

  private syncSessionTime(): void {
    if (this.elapsedScormTime === undefined) return;

    API.LMSSetValue(
      AppConstants.CORE_SESSION_TIME,
      getFormattedTimeString(this.elapsedScormTime)
    );
  }

  onContentClose(): void {
    const fromLearningResources: boolean = JSON.parse(
      this.route.snapshot.queryParamMap.get(
        AppConstants.FROM_LEARNING_RESOURCES
      )
    );

    if (fromLearningResources) {
      this.originalWindowClose.call(window);
      return;
    }

    this.compRef?.instance.closeScorm();
  }

  ngOnDestroy(): void {
    window.close = this.originalWindowClose;
    this.flushScormOnExit();

    const sub$ = this.publicUserSessionService.setIsAllowAccessContent$(AppConstants.FALSE).subscribe();
    this.addSubscription(sub$);
    this._routeListener.unsubscribe();
    this.stopTimer();

    if(this.iframeSub)
      this.iframeSub.unsubscribe();

    if(this.tokenRefreshSub$)
      this.tokenRefreshSub$.unsubscribe();
  }
}
